import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { ratingRelationshipLabel, selectRatingEvidence } from '../utils/ratingEvidence';

const EMPTY_MESSAGES = {
  excluded: 'No rating matched the validated company identity or a verified related entity.',
  not_found: 'No external credit rating was found in the latest search.',
  unavailable: 'External rating information is currently unavailable.',
  pending: 'External ratings are awaiting issuer validation.',
};

export default function ExternalRatings({ company, managedReceivables = false }) {
  const { ratings, validation } = selectRatingEvidence(company);
  return (
    <Box sx={{ mx: 3, mt: 3, p: 2.5, border: '1px solid #D9E5F7', borderRadius: 2.5, bgcolor: '#fff', boxShadow: '0 2px 8px rgba(25, 42, 86, 0.04)' }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2, mb: 2.25 }}>
        <Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#1A2A56' }}>External Ratings</Typography>
          <Typography sx={{ mt: 0.4, fontSize: 13, color: '#64748B' }}>Long-term ratings with a validated issuer relationship.</Typography>
        </Box>
        {ratings.length > 0 && <Chip size="small" label={`${ratings.length} accepted ${ratings.length === 1 ? 'rating' : 'ratings'}`} sx={{ bgcolor: '#EEF6FF', color: '#1E5FAF', fontWeight: 700, border: '1px solid #CFE2FF' }} />}
      </Box>
      {ratings.length ? (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.5 }}>
          {ratings.map((rating, index) => (
            <Box key={`${rating.Agency}-${rating.rated_entity_name}-${index}`} sx={{ minWidth: 0, p: 1.75, border: '1px solid #E5EDF8', borderRadius: 2, bgcolor: '#FBFDFF', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ minWidth: 0, fontSize: 14, fontWeight: 700, color: '#243B67' }}>{rating.Agency}</Typography>
                <Chip label={rating.Long_Term_Rating} size="small" sx={{ height: 26, bgcolor: '#FFF5DE', color: '#9A6500', border: '1px solid #F7D78D', fontWeight: 800, fontSize: 13 }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#8090A8' }}>Rated issuer</Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: '#3C4F70' }}>{rating.rated_entity_name}</Typography>
                <Typography sx={{ fontSize: 12, color: '#64748B' }}>{ratingRelationshipLabel(rating.validation.relationship)}</Typography>
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Box>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: '#8090A8' }}>Outlook</Typography>
                  <Typography sx={{ mt: 0.2, fontSize: 13.5, fontWeight: 600, color: '#3C4F70' }}>{rating.Outlook || 'Not stated'}</Typography>
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', color: '#8090A8' }}>As of</Typography>
                  <Typography sx={{ mt: 0.2, fontSize: 13.5, fontWeight: 600, color: '#3C4F70' }}>{rating.Last_Update || 'Not stated'}</Typography>
                </Box>
              </Box>
              <Box component="a" href={rating.source_url} target="_blank" rel="noreferrer" sx={{ mt: 0.25, display: 'inline-flex', alignItems: 'center', gap: 0.5, width: 'fit-content', color: '#246BCE', fontSize: 12.5, fontWeight: 700, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
                View rating action <OpenInNewIcon sx={{ fontSize: 14 }} />
              </Box>
            </Box>
          ))}
        </Box>
      ) : <Typography sx={{ color: '#64748B' }}>{EMPTY_MESSAGES[validation.status]}{managedReceivables ? ' Review source coverage before requesting provider updates.' : ''}</Typography>}
    </Box>
  );
}
